import { z } from 'zod';
import { reusePolicySchema } from './reuse';
import { audienceSchema, audienceScopeSchema } from './audience';
import type { FrozenQuestion, TakerQuestion } from './quizzes';
import { LEVELS, tagsSchema, topicsSchema } from './topics';

/*
 * On-demand courses: Course → Modules → Lessons → Blocks. Authors edit a
 * draft; publishing freezes it into a version (like quizzes), and learners
 * stay on the version they enrolled in. Files live in course_assets and are
 * referenced by id; learners get short-lived signed URLs, never raw keys.
 */

// ─── Assets ────────────────────────────────────────────────────────────

export const ASSET_KINDS = [
  'video',
  'audio',
  'image',
  'pdf',
  'slides',
  'document',
  'spreadsheet',
  'notebook',
  'code',
  'markdown',
  'archive',
  'other',
] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

/** Upload limits. Videos are short lectures (about 5 minutes), served whole. */
export const ASSET_MAX_BYTES: Record<AssetKind, number> = {
  video: 500 * 1024 * 1024,
  audio: 100 * 1024 * 1024,
  image: 15 * 1024 * 1024,
  pdf: 100 * 1024 * 1024,
  slides: 200 * 1024 * 1024,
  document: 50 * 1024 * 1024,
  spreadsheet: 50 * 1024 * 1024,
  notebook: 25 * 1024 * 1024,
  code: 2 * 1024 * 1024,
  markdown: 2 * 1024 * 1024,
  archive: 200 * 1024 * 1024,
  other: 100 * 1024 * 1024,
};

const EXTENSION_KINDS: Record<string, AssetKind> = {
  mp4: 'video',
  mov: 'video',
  webm: 'video',
  m4v: 'video',
  mp3: 'audio',
  m4a: 'audio',
  wav: 'audio',
  ogg: 'audio',
  aac: 'audio',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  svg: 'image',
  pdf: 'pdf',
  ppt: 'slides',
  pptx: 'slides',
  key: 'slides',
  odp: 'slides',
  doc: 'document',
  docx: 'document',
  odt: 'document',
  rtf: 'document',
  txt: 'document',
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  csv: 'spreadsheet',
  ods: 'spreadsheet',
  ipynb: 'notebook',
  py: 'code',
  js: 'code',
  ts: 'code',
  tsx: 'code',
  jsx: 'code',
  java: 'code',
  go: 'code',
  rs: 'code',
  c: 'code',
  cpp: 'code',
  h: 'code',
  cs: 'code',
  rb: 'code',
  php: 'code',
  sh: 'code',
  sql: 'code',
  yaml: 'code',
  yml: 'code',
  json: 'code',
  toml: 'code',
  html: 'code',
  css: 'code',
  md: 'markdown',
  mdx: 'markdown',
  zip: 'archive',
  tar: 'archive',
  gz: 'archive',
};

export function assetKindFor(filename: string): AssetKind {
  const ext = filename.toLowerCase().split('.').pop() ?? '';
  return EXTENSION_KINDS[ext] ?? 'other';
}

const EXTENSION_TYPES: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  key: 'application/vnd.apple.keynote',
  odp: 'application/vnd.oasis.opendocument.presentation',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  odt: 'application/vnd.oasis.opendocument.text',
  rtf: 'application/rtf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  csv: 'text/csv; charset=utf-8',
  ipynb: 'application/json',
  json: 'application/json',
  zip: 'application/zip',
  gz: 'application/gzip',
  tar: 'application/x-tar',
};

/**
 * The content type files are stored and served with, chosen from the
 * extension (never the browser's claim). Anything that could run in a
 * browser (HTML, scripts, Markdown, code) is stored as plain text.
 */
export function contentTypeFor(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop() ?? '';
  const known = EXTENSION_TYPES[ext];
  if (known) return known;
  const kind = assetKindFor(filename);
  return kind === 'code' || kind === 'markdown' || ext === 'txt'
    ? 'text/plain; charset=utf-8'
    : 'application/octet-stream';
}

/** Kinds converted to PDF for inline previews (LibreOffice). */
export const PREVIEWABLE_KINDS: readonly AssetKind[] = ['slides', 'document', 'spreadsheet'];

export const ASSET_STATUSES = ['uploading', 'scanning', 'ready', 'infected', 'failed'] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const uploadRequestSchema = z.object({
  filename: z.string().trim().min(1).max(200),
  contentType: z.string().trim().min(1).max(200),
  sizeBytes: z.number().int().positive(),
});
export type UploadRequest = z.infer<typeof uploadRequestSchema>;

// ─── Blocks ────────────────────────────────────────────────────────────

const id = z.string().min(1).max(40);
const assetId = z.string().uuid();
const url = z.string().trim().url().max(2000);

export const CALLOUT_VARIANTS = ['info', 'tip', 'warning', 'success'] as const;

export const blockSchema = z.discriminatedUnion('type', [
  z.object({ id, type: z.literal('markdown'), markdown: z.string().max(100_000) }),
  z.object({
    id,
    type: z.literal('callout'),
    variant: z.enum(CALLOUT_VARIANTS).default('info'),
    title: z.string().trim().max(200).optional(),
    markdown: z.string().max(20_000),
  }),
  z.object({ id, type: z.literal('video'), assetId, title: z.string().max(200).optional() }),
  z.object({ id, type: z.literal('audio'), assetId, title: z.string().max(200).optional() }),
  z.object({
    id,
    type: z.literal('image'),
    assetId,
    alt: z.string().trim().max(500).default(''),
    caption: z.string().trim().max(500).optional(),
  }),
  /** PDF, slides, Word, Excel: inline preview (PDF or converted) plus optional download. */
  z.object({
    id,
    type: z.literal('document'),
    assetId,
    title: z.string().max(200).optional(),
    allowDownload: z.boolean().default(true),
  }),
  z.object({ id, type: z.literal('notebook'), assetId, title: z.string().max(200).optional() }),
  z.object({
    id,
    type: z.literal('code'),
    /** Either an uploaded file or inline source. */
    assetId: assetId.optional(),
    source: z.string().max(100_000).optional(),
    filename: z.string().max(200).optional(),
    language: z.string().max(40).optional(),
  }),
  z.object({
    id,
    type: z.literal('files'),
    title: z.string().max(200).optional(),
    assetIds: z.array(assetId).min(1).max(50),
  }),
  /** YouTube, Vimeo, Loom, Google Docs/Slides/Forms, Figma, CodePen (allowlist). */
  z.object({ id, type: z.literal('embed'), url, title: z.string().max(200).optional() }),
  z.object({
    id,
    type: z.literal('link'),
    url,
    title: z.string().max(300).optional(),
    description: z.string().max(1000).optional(),
    imageUrl: url.optional(),
    siteName: z.string().max(200).optional(),
  }),
  /** Practice questions from a question bank; ungraded, feedback like a self-assessment. */
  z.object({
    id,
    type: z.literal('knowledge_check'),
    title: z.string().max(200).optional(),
    questionIds: z.array(z.string().uuid()).min(1).max(20),
  }),
]);
export type Block = z.infer<typeof blockSchema>;
export type BlockType = Block['type'];

/** Blocks that must be finished before a lesson can be completed. */
export const MEDIA_COMPLETE_RATIO = 0.9;

// ─── Lessons, modules, courses ─────────────────────────────────────────

export const lessonInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  summary: z.string().trim().max(1000).optional(),
  /** Optional lessons don't count toward completion. */
  optional: z.boolean().default(false),
  estimatedMinutes: z.number().int().min(1).max(600).optional(),
  blocks: z.array(blockSchema).max(100).default([]),
});
export type LessonInput = z.infer<typeof lessonInputSchema>;

export const outlineModuleSchema = z.object({
  id,
  title: z.string().trim().min(1).max(200),
  summary: z.string().trim().max(1000).optional(),
  lessonIds: z.array(z.string().uuid()).max(200),
});
export const courseOutlineSchema = z.object({
  modules: z.array(outlineModuleSchema).max(50),
});
export type CourseOutline = z.infer<typeof courseOutlineSchema>;

export const COURSE_LEVELS = LEVELS;

export const courseSettingsSchema = z.object({
  certificate: z.boolean().default(true),
  /** A published quiz to pass at the end (same owner or Nirvik's library). */
  finalQuizId: z.string().uuid().nullable().default(null),
});
export type CourseSettings = z.infer<typeof courseSettingsSchema>;

export const courseInputSchema = z.object({
  title: z.string().trim().min(3).max(200),
  summary: z.string().trim().max(500).optional(),
  description: z.string().max(20_000).optional(),
  outcomes: z.array(z.string().trim().min(1).max(300)).max(20).default([]),
  level: z.enum(COURSE_LEVELS).default('beginner'),
  estimatedMinutes: z.number().int().min(1).max(10_000).optional(),
  tags: tagsSchema,
  topics: topicsSchema,
  coverAssetId: z.string().uuid().nullable().optional(),
  audience: audienceSchema.default('org'),
  audienceScope: audienceScopeSchema.nullable().optional(),
  reuse: reusePolicySchema.nullable().optional(),
  settings: courseSettingsSchema.default({}),
});
export type CourseInput = z.infer<typeof courseInputSchema>;

// ─── What learners receive ─────────────────────────────────────────────

/** A knowledge check as frozen into a published version (answers kept on the server). */
export type FrozenKnowledgeCheck = Extract<Block, { type: 'knowledge_check' }> & {
  questions: FrozenQuestion[];
};
/** The same, for learners. */
export type LearnerKnowledgeCheck = Extract<Block, { type: 'knowledge_check' }> & {
  questions: TakerQuestion[];
};

/** Asset metadata sent with lessons; URLs are signed per request. */
export interface LearnerAsset {
  id: string;
  kind: AssetKind;
  filename: string;
  contentType: string;
  sizeBytes: number;
  url: string;
  /** Inline PDF preview for Office files, when converted. */
  previewUrl: string | null;
}

export type LearnerBlock = Exclude<Block, { type: 'knowledge_check' }> | LearnerKnowledgeCheck;

export interface LessonProgressState {
  completedAt: string | null;
  /** Fraction watched or listened, per video/audio block id. */
  media: Record<string, number>;
  /** Questions answered in knowledge checks, per block id. */
  checks: Record<string, string[]>;
}
